import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
  type ReactNode,
} from 'react';
import './toast.css';

export type ToastType = 'success' | 'info' | 'warning' | 'error';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastItem {
  id: string;
  type: ToastType;
  message: string;
  /** Auto-dismiss duration in ms. Default 4000. Pass 0 for persistent (manual dismiss). */
  duration?: number;
  action?: ToastAction;
}

export interface ToastContextValue {
  /** Add a toast. Returns the generated id so callers can dismiss it programmatically. */
  addToast: (toast: Omit<ToastItem, 'id'>) => string;
  /** Remove a toast by id. */
  removeToast: (id: string) => void;
}

export interface ToastProviderProps {
  children: ReactNode;
  /** Maximum toasts shown simultaneously. Default 3. Older toasts are dropped FIFO. */
  maxCount?: number;
}

const ICONS: Record<ToastType, string> = {
  success: '✓',
  info: 'ℹ',
  warning: '⚠',
  error: '⚠',
};

// Toasts auto-dismiss after this many ms. Bumped from 4000 → 6000 so success
// toasts stay long enough that E2E visibility polls (10–15s assertion timeout)
// reliably catch them on slow networks; matches ERROR_DURATION for consistency.
const DEFAULT_DURATION = 6000;
const ERROR_DURATION = 6000;

function noop(): void {
  /* no-op default before provider mounts */
}

export const ToastContext = createContext<ToastContextValue>({
  addToast: () => {
    return '';
  },
  removeToast: noop,
});

let idCounter = 0;
function generateId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  idCounter += 1;
  return `toast-${Date.now()}-${idCounter}`;
}

export function ToastProvider({ children, maxCount = 3 }: ToastProviderProps): JSX.Element {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const removeToast = useCallback((id: string): void => {
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback(
    (toast: Omit<ToastItem, 'id'>): string => {
      const id = generateId();
      const duration =
        typeof toast.duration === 'number'
          ? toast.duration
          : toast.type === 'error'
            ? ERROR_DURATION
            : DEFAULT_DURATION;
      const next: ToastItem = { ...toast, id, duration };

      setToasts((prev) => {
        const trimmed = prev.length >= maxCount ? prev.slice(prev.length - maxCount + 1) : prev;
        return [...trimmed, next];
      });

      if (duration > 0) {
        const timer = setTimeout(() => {
          removeToast(id);
        }, duration);
        timers.current.set(id, timer);
      }

      return id;
    },
    [maxCount, removeToast],
  );

  useEffect(() => {
    const liveTimers = timers.current;
    return () => {
      liveTimers.forEach((t) => clearTimeout(t));
      liveTimers.clear();
    };
  }, []);

  const contextValue = useMemo<ToastContextValue>(
    () => ({ addToast, removeToast }),
    [addToast, removeToast],
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastRegion toasts={toasts} onDismiss={removeToast} />
    </ToastContext.Provider>
  );
}

interface ToastRegionProps {
  toasts: ToastItem[];
  onDismiss: (id: string) => void;
}

function ToastRegion({ toasts, onDismiss }: ToastRegionProps): JSX.Element {
  return (
    <div
      className="toast-region"
      role="region"
      aria-label="Notifications"
      aria-live="polite"
      aria-atomic="false"
      data-testid="toast-region"
    >
      {toasts.map((t) => (
        <Toast key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

interface ToastProps {
  toast: ToastItem;
  onDismiss: (id: string) => void;
}

function Toast({ toast, onDismiss }: ToastProps): JSX.Element {
  const handleDismiss = useCallback(() => {
    onDismiss(toast.id);
  }, [toast.id, onDismiss]);

  const role = toast.type === 'error' ? 'alert' : 'status';

  return (
    <div
      className={`toast toast--${toast.type}`}
      role={role}
      data-testid={`toast-${toast.id}`}
    >
      <span className="toast__icon" aria-hidden="true">
        {ICONS[toast.type]}
      </span>
      <p className="toast__message">{toast.message}</p>
      {toast.action && (
        <button
          type="button"
          className="toast__action"
          onClick={() => {
            toast.action?.onClick();
            handleDismiss();
          }}
        >
          {toast.action.label}
        </button>
      )}
      <button
        type="button"
        className="toast__dismiss"
        onClick={handleDismiss}
        aria-label="Dismiss notification"
      >
        ×
      </button>
    </div>
  );
}

export function useToast(): ToastContextValue {
  return useContext(ToastContext);
}
